<template>
  <div class="quick-add">
    <button
      v-if="product"
      class="quick-add__add-to-cart"
      :class="{
        [$class('success')]: state === 'added',
        [$class('error')]: state === 'error',
        'quick-add__add-to-cart--label': Boolean(quickAddLabel),
      }"
      :disabled="!isCardAvailable || (disableCta && state !== 'adding')"
      type="button"
      @click.prevent.stop="checkAddToCart"
    >
      <span
        v-if="!quickAddLabel"
        class="visually-hidden"
        v-text="$string('product.cta.add_to_cart')"
      />
      <span
        v-else
        class="quick-add__label text-mobile-paragraph-m-bold text-desktop-paragraph-m-bold"
        v-text="quickAddLabel"
      />
      <component :is="addToCartIcon" />
    </button>

    <teleport to="body">
      <overlay
        v-if="
          (useProductState ||
            variantsShowQatcOverlay ||
            template === 'card-compact') &&
          product &&
          selectedVariant
        "
        :ref="`quickAddModal-${product.handle}`"
        block-class="quick-add-select-options"
        direction="up"
        :namespace="modalNamespace"
        type="shelf-modal"
        shelf-close
        :ignore-close="dropdownIsOpen"
      >
        <template #title>
          <span
            class="quick-add__overlay-title"
            v-text="$string('product.quick_add.modal_title')"
          />
        </template>
        <template #body="overlay">
          <a class="quick-add-select-options__content" :href="product.url">
            <div class="quick-add-select-options__image-container">
              <responsive-image
                class="quick-add-select-options__image"
                :image="featuredImage"
                ratio="1:1"
                sizes="(min-width: 1024px) 178px, 120px"
                widths="178, 356, 534"
              />
            </div>

            <div class="quick-add-select-options__title">
              <p
                class="product-card-select__title text-mobile-paragraph-m-bold text-desktop-paragraph-m-bold"
                v-text="product.title"
              />

              <product-prices
                class="product-card-select__prices"
                :compare-at-price="selectedVariant?.compareAtPrice?.amount"
                :discount-logic="getDiscountLogic(selectedVariant)"
                :price="selectedVariant?.price?.amount"
                template="card"
                :unit-price="selectedVariant?.unitPrice?.amount"
                :unit-price-measurement="selectedVariant?.unitPriceMeasurement"
                :is-gift-card="product.giftCard"
              />
            </div>
          </a>

          <form
            class="quick-add-select-options__form"
            @submit.prevent="handleUpdateSelectedVariant"
          >
            <product-options
              v-if="overlay.isActive || isVariantSelectorActive"
              :key="`quickAddModal-${product.handle}`"
              class="product-form__options"
              :loading="loading"
              :product="product"
              :section="{
                id: product.id,
                optionStylingTile: $variable(
                  'product.overlayProductOptionsTile',
                ),
                optionStylingImageTile: [],
              }"
              :variant="selectedVariant"
              :variant-from-url="false"
              hide-count-label
              hide-portions-label
              hide-sold-out-label
              hide-flavor-image-on-button
              hide-flavor-badge-on-button
              style-full-size
              :hide-labels="false"
              @update-variant="handleUpdateOption"
              @dropdown-toggled="
                (isOpen) => {
                  dropdownIsOpen = isOpen;
                }
              "
              @variant-selector-namespace="
                (namespace) => {
                  variantSelectorNamespace = namespace;
                }
              "
            />

            <notify-me
              v-if="showNotify"
              ref="notifyMe"
              class="quick-add-select-options__notify-me"
              :variant-id="selectedVariant.id"
              :product-id="product.id"
              @update-success="handleNotifyMeSuccess"
            />

            <div
              v-if="showCta"
              class="quick-add-select-options__button-wrapper"
            >
              <btn
                class="quick-add-select-options__save"
                :class="{
                  [`quick-add-${template}`]: template,
                  'button--notify-me': variant && !selectedVariant.available,
                }"
                :label="addToCartText"
                modifiers="secondary cta center no-hover"
                :show-state-icon="state === 'add_notify'"
                :show-icon="layerAddToCartIcon !== ''"
                type="submit"
                @click="handleAddToCart"
              >
                <template #icon>
                  <component :is="layerAddToCartIcon" />
                </template>

                <template #stateIcon>
                  <icon-loading />
                </template>
              </btn>
            </div>
          </form>
        </template>
      </overlay>
    </teleport>
  </div>
</template>

<script>
/**
 * Vue: quick-add (quick-add)
 * -----------------------------------------------------------------------------
 * Quick add to card button for product cards.
 * - Component settings: snippet / dynamic / async / scroll
 *
 */
import { mapActions, mapState } from 'pinia';
import useToastNotificationsStore from '~stores/toast-notifications';
import useOverlaysStore from '~stores/overlays';
import useCartStore from '~stores/cart';
import useDiscountCampaignsStore from '~stores/discount-campaigns';

import Btn from '~global/btn/btn';
import IconAdd from '~icons/general/add.svg';
import IconCheck from '~icons/general/check.svg';
import IconCross from '~icons/general/cross.svg';
import IconChevronDown from '~icons/directional-navigation/chevron/down.svg';
import IconEmail from '~icons/general/email.svg';
import IconLoading from '~icons/general/loading.svg';
import NotifyMe from '~async/product-form/notify-me';
import Overlay from '~global/overlay/overlay';
import ProductOptions from '~async/product-form/product-options';
import ProductBadges from '~global/product-badges/product-badges';
import ProductPrices from '~global/product-prices/product-prices';
import ResponsiveImage from '~global/images/responsive-image';

import { formatTrackingItem } from '~/format/format-tracking-item';
import { computeDiscountLogic, emitUpdateVariant } from '~/helpers/vue';
import { getMaxQuantityErrorMessage } from '~/helpers/general';

export default {
  name: 'QuickAdd',

  components: {
    Btn,
    IconAdd,
    IconCheck,
    IconChevronDown,
    IconCross,
    IconEmail,
    IconLoading,
    NotifyMe,
    Overlay,
    ProductOptions,
    ProductBadges,
    ProductPrices,
    ResponsiveImage,
  },

  inject: {
    variantsShowQatcOverlay: {
      from: 'variantsShowQatcOverlay',
      default: true,
    },
    sectionId: {
      from: 'sectionId',
      default: false,
    },
  },

  props: {
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    variant: {
      type: [Boolean, Object],
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    location: {
      type: String,
      default: 'Quick Add To Cart',
    },
    useProductState: {
      type: Boolean,
      default: false,
    },
    campaignCard: {
      type: Boolean,
      default: false,
    },
    freebieHandle: {
      type: [Boolean, String],
      default: false,
    },
    freebieGroupUid: {
      type: String,
      default: null,
    },
    showPageToast: {
      type: Boolean,
      default: true,
    },
    showCartToast: {
      type: Boolean,
      default: false,
    },
    template: {
      type: String,
      default: '',
    },
    quickAddLabel: {
      type: String,
      default: '',
    },
    slideIndex: {
      type: Number,
      default: 0,
    },
  },

  emits: {
    'track-click': () => true,
    'update-variant': emitUpdateVariant,
    'added-to-cart': (payload) => typeof payload === 'number',
  },

  data() {
    return {
      // only 9 characters to keep it short, since it's used in the DOM
      uid: Math.random().toString(36).slice(2, 11),
      state: 'ready',
      selected: true,
      selectedVariant: false,
      showCta: true,
      dropdownIsOpen: false,
      variantSelectorNamespace: '',
    };
  },

  computed: {
    ...mapState(useDiscountCampaignsStore, {
      getCampaignDiscount: 'getCampaignDiscount',
    }),
    ...mapState(useCartStore, {
      cartStatus: 'status',
      depositItemUpdate: 'depositItemUpdate',
    }),
    ...mapState(useOverlaysStore, ['getActiveOverlay']),

    isCardAvailable() {
      // If the product is a campaign card or uses product state, we check the variants for availability.
      if (this.useProductState || this.campaignCard) {
        return this.product?.variants?.some(
          (variant) =>
            variant.available && variant.availableState === 'available',
        );
      }

      const state = this.initialSelectedVariant;

      return !(
        state?.availableState === 'back-soon' ||
        state?.availableState === 'sold-out' ||
        state?.availableState === 'hidden'
      );
    },

    /**
     * Compute add to cart icon.
     * @returns {String}
     */
    addToCartIcon() {
      switch (this.state) {
        case 'adding':
          return 'icon-loading';
        case 'added':
          return 'icon-check';
        case 'error':
          return 'icon-cross';
        default:
          return this.quickAddLabel ? null : 'icon-add';
      }
    },

    /**
     * Compute cta button icon.
     * @returns {String}
     */
    layerAddToCartIcon() {
      if (this.showNotify) {
        return 'icon-email';
      }
      return '';
    },

    disableCta() {
      return Boolean(
        this.selectedVariant?.id &&
        this.cartStatus.items.includes(this.selectedVariant.id),
      );
    },

    /**
     * Compute add to cart text.
     * @returns {String}
     */
    addToCartText() {
      if (!this.selectedVariant) {
        return this.$string('product.cta.select_variant');
      }

      if (this.variant && !this.selectedVariant?.available) {
        return this.$string('product.cta.notify_me');
      }

      return this.$string('product.cta.to_cart');
    },

    /**
     * Checks whether to use variant first image.
     * - Use variant featured image (grey background) in product grid.
     * @returns {Object}
     */
    featuredImage() {
      if (this.selectedVariant) {
        if (
          this.selectedVariant.media &&
          this.selectedVariant.media.length > 0
        ) {
          return this.selectedVariant.media[0].image;
        }

        if (this.selectedVariant.localizedImage) {
          return this.selectedVariant.localizedImage;
        }

        if (
          this.selectedVariant.featuredImage &&
          Object.keys(this.selectedVariant.featuredImage).length > 0
        ) {
          return this.selectedVariant.featuredImage;
        }
      }

      if (this.product.media && this.product.media.length > 0) {
        return this.product.media[0].image;
      }

      return this.product.featuredImage;
    },

    /**
     * Checks if the current variant is available.
     * @returns {boolean} True if the variant exists and is available.
     */
    isSelectedVariantAvailable() {
      return this.selectedVariant?.available;
    },

    /**
     * Checks if the current variant's state is either 'back-soon'
     * or 'sold-out'.
     * @returns {boolean} True if the variant's availableState is 'back-soon'
     * or 'sold-out'.
     */
    isSelectedVariantBackSoonOrSoldOut() {
      return ['back-soon', 'sold-out'].includes(
        this.selectedVariant?.availableState,
      );
    },

    hasOnlyOneVariant() {
      return (
        this.product?.variants?.filter(
          (variant) =>
            variant.available && variant.availableState === 'available',
        ).length === 1
      );
    },

    /**
     * Checks if notify me form should be shown instead of add to cart.
     * @returns {boolean} True if the variant exists and is available.
     */
    showNotify() {
      return (
        !this.isSelectedVariantAvailable ||
        this.isSelectedVariantBackSoonOrSoldOut
      );
    },

    modalNamespace() {
      const sectionIdSubstr = this.sectionId ? `${this.sectionId}-` : '';
      return `quickAddModal-${sectionIdSubstr}${this.product.handle}-${this.variant.id}-${this.uid}`;
    },

    isVariantSelectorActive() {
      return this.getActiveOverlay?.namespace === this.variantSelectorNamespace;
    },

    /**
     * Use the product's default selectable variant for product-based cards.
     * Keep the passed variant for variant-specific cards.
     * @returns {Boolean|Object}
     */
    initialSelectedVariant() {
      if (this.useProductState || !this.variant) {
        return this.product?.selectedOrFirstAvailableVariant;
      }

      return this.variant;
    },
  },

  watch: {
    /**
     * Watch current variant to update form's selected variant.
     */
    selectedVariant() {
      this.soldOut = Boolean(!this.selectedVariant?.available);

      this.showCta =
        !this.$refs.notifyMe ||
        (this.$refs.notifyMe &&
          !this.$refs.notifyMe.submittedVariantIds.includes(
            this.selectedVariant?.id,
          ));
    },

    /**
     * Watch product to update current variant.
     */
    product() {
      this.selectedVariant = this.initialSelectedVariant;
    },
    /**
     * Watch variant to update current variant.
     */
    variant() {
      this.selectedVariant = this.initialSelectedVariant;
    },
  },

  mounted() {
    // Set initial variant on mount.
    this.selectedVariant = this.initialSelectedVariant;

    this.soldOut = Boolean(!this.selectedVariant?.available);
  },

  methods: {
    ...mapActions(useCartStore, {
      addItem: 'addItem',
      setDepositItemUpdate: 'setDepositItemUpdate',
      getCartAttributeCode: 'getCartAttributeCode',
    }),
    ...mapActions(useToastNotificationsStore, ['addToast']),
    ...mapActions(useOverlaysStore, {
      openOverlay: 'open',
      closeOverlay: 'close',
    }),

    /**
     * Handle update variant event.
     * @param {Object} variant - Emitted variant object.
     */
    handleUpdateOption(variant) {
      this.selectedVariant = variant;
      this.$emit('update-variant', variant);
    },

    /**
     * Reset the overlay variant back to the initial card variant.
     */
    resetSelectedVariant() {
      this.selectedVariant = this.initialSelectedVariant;
    },

    /**
     * Get discount logic.
     * @param {Boolean|Object} variant - Variant object to use.
     * @returns {Object}
     */
    getDiscountLogic(variant) {
      return computeDiscountLogic({
        discountPreview: false,
        onSale: variant?.compareAtPriceDiscount,
        product: this.product,
        variant,
      });
    },

    /**
     * Checks on first button click if product should go directly to cart.
     * Or if we have to show variant selection layer.
     *
     * @param {Object} event - Event data object.
     * @param {Boolean} forceOverlay - Flag to force show overlay.
     */
    async checkAddToCart(event, forceOverlay = false) {
      if (this.state === 'adding') {
        return;
      }
      this.$emit('track-click');

      if (
        !forceOverlay &&
        (this.product.hasOnlyDefaultVariant ||
          this.hasOnlyOneVariant ||
          (!this.useProductState && !this.variantsShowQatcOverlay))
      ) {
        await this.handleAddToCart();
      } else {
        this.openOverlay({
          callback: () => this.resetSelectedVariant(),
          ignoreDismissed: true,
          namespace: this.modalNamespace,
        });
      }
    },

    /**
     * Handle add to cart submission.
     */
    async handleAddToCart() {
      let depositVariantId;
      if (!this.selectedVariant) {
        return;
      }

      /**
       * Notify me.
       * - Uses child component method.
       */
      if (!this.selectedVariant.available) {
        try {
          this.state = 'add_notify';
          await this.$refs.notifyMe.handleNotifyMe();
          this.state = 'ready';
        } catch (error) {
          this.state = 'error';
          cnvs.ReportError('Failed to submit notify me', error);
        }

        return;
      }

      const item = {
        id: this.selectedVariant.id,
        quantity: 1,
        hasLocalizedImage: Boolean(this.selectedVariant.localizedImage),
        productId: this.product.id,
        productMaxQuantity: this.product.maxQuantity,
        variantMaxQuantity: this.selectedVariant.maxQuantity,
      };

      if (this.freebieHandle) {
        item.properties = {
          [this.$variable('discount.freebies.property')]: this.freebieHandle,
        };
      }

      if (this.freebieGroupUid) {
        if (!item.properties) {
          item.properties = {};
        }
        item.properties[this.$variable('discount.freebies.propertyGroup')] =
          this.freebieGroupUid;
      }

      const items = [item];

      if (
        this.selectedVariant.canDeposit === 'true' &&
        this.$variable('store.country') === 'DE'
      ) {
        depositVariantId = this.selectedVariant.id;
        this.setDepositItemUpdate([
          ...this.depositItemUpdate,
          depositVariantId,
        ]);
        const depositProduct = {
          id: this.$variable('product.depositProductID'),
          quantity: this.selectedVariant.canDepositAmount,
        };
        items.push(depositProduct);
      }

      if (!this.product.hasOnlyDefaultVariant) {
        this.closeOverlay(this.modalNamespace);
      }

      /**
       * Add to cart.
       */
      try {
        this.state = 'adding';
        await this.addItem(items);
        this.state = 'added';
        if (this.showPageToast) {
          this.showPageToastSuccess();
        } else if (this.showCartToast) {
          this.showCartToastSuccess();
        }

        // Tracking event add qty to item in cart
        this.handleTrackingAddItem();

        // ESN-58: bubble event up to trigger correct goal
        this.$emit('added-to-cart', this.slideIndex);

        await this.resetState();
      } catch (error) {
        this.state = 'error';

        // Handle maxQuantity errors
        if (error.maxQuantityError) {
          const message = getMaxQuantityErrorMessage(
            error.maxQuantityType,
            error.maxQuantityValue,
          );

          this.addToast({
            title: message,
            id: 'max-quantity-limit-error',
            type: 'error',
          });
          this.state = 'ready';
          await this.resetState();
          return;
        }

        if (this.showPageToast) {
          this.showPageToastError(error.description);
        }

        await this.resetState();

        if (error.description) {
          return;
        }

        cnvs.ReportError('Failed to add product sample to cart', error);
      }

      this.setDepositItemUpdate(
        this.depositItemUpdate.filter((id) => id !== depositVariantId),
      );
    },

    /**
     * Handle update on the notify me component - if requests has success.
     * @param {Boolean} success - Wether the request was successful or not.
     */
    handleNotifyMeSuccess(success) {
      if (success) {
        this.showCta = false;
      } else {
        this.state = 'error';
      }
    },

    /**
     * If add was successful, show success toast.
     */
    showPageToastSuccess() {
      this.addToast({
        title: this.$string('toast.quick_add_success'),
        id: 'quick-add-success',
        ctaText: this.$string('toast.open_cart'),
        type: 'success',
        callback: () => {
          this.openOverlay({
            component: 'cart-drawer',
            ignoreDismissed: true,
            namespace: 'cartDrawer',
          });
        },
      });
    },

    /**
     * if somethign went wrong, show error toast with response message.
     * @param {String} errorMessage - Error description
     */
    showPageToastError(errorMessage) {
      this.addToast({
        title: errorMessage,
        id: 'quick-add-error',
        ctaText: this.$string('toast.open_cart'),
        type: 'error',
        callback: () => {
          this.openOverlay({
            component: 'cart-drawer',
            ignoreDismissed: true,
            namespace: 'cartDrawer',
          });
        },
      });
    },

    showCartToastSuccess() {
      this.addToast({
        id: 'freebie-manual-add-success',
        type: 'success',
        customClass: 'drawer',
        showCheckMark: false,
        title: this.$string('cart.freebies.added_message'),
      });
    },

    /**
     * Tracking event add qty to item in cart.
     */
    handleTrackingAddItem() {
      const campaignDiscount = this.getCampaignDiscount(
        this.discountLogic,
        'active',
      );
      const discountObject = {
        discountAmount: campaignDiscount?.amount,
        calculationType: campaignDiscount?.calculationType,
        discountCode: this.getCartAttributeCode(),
      };

      const deliveryTime = this.$string('product.delivery_time', {
        replace: { time: this.$variable('product.deliveryTime') },
      });
      const currencyCode = this.$variable('store.currency');

      const item = formatTrackingItem({
        product: this.product,
        variant: this.variant,
        template: 'Quick Add To Cart',
        discountObject,
        deliveryTime,
        currencyCode,
        quantity: this.quantity,
      });

      cnvs.EventBus.emit('dataLayer:add_to_cart', {
        event: 'add_to_cart',
        currency: cnvs.store.currency,
        location: this.location,
        items: [item],
      });
    },

    /**
     * Reset product form state after adding to cart after message delay.
     */
    async resetState() {
      await new Promise((resolve) => {
        setTimeout(resolve, this.$timing('message'));
      });

      this.state = 'ready';
    },
  },
};
</script>

<style lang="scss">
@import './quick-add';
</style>
